import type { PresignedRequest } from "@led-signage/shared/common";

const DEFAULT_CHUNK_BYTES = 4 * 1024 * 1024;
const SHA256_BLOCK_BYTES = 64;

const ROUND_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
  0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
  0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
  0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
  0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotateRight(value: number, count: number): number {
  return (value >>> count) | (value << (32 - count));
}

class IncrementalSha256 {
  private readonly state = new Uint32Array([
    0x6a09e667,
    0xbb67ae85,
    0x3c6ef372,
    0xa54ff53a,
    0x510e527f,
    0x9b05688c,
    0x1f83d9ab,
    0x5be0cd19,
  ]);
  private readonly pending = new Uint8Array(SHA256_BLOCK_BYTES);
  private pendingLength = 0;
  private totalBytes = 0;

  update(input: Uint8Array): void {
    this.totalBytes += input.byteLength;
    let offset = 0;

    if (this.pendingLength > 0) {
      const copied = Math.min(
        SHA256_BLOCK_BYTES - this.pendingLength,
        input.byteLength,
      );
      this.pending.set(input.subarray(0, copied), this.pendingLength);
      this.pendingLength += copied;
      offset += copied;
      if (this.pendingLength === SHA256_BLOCK_BYTES) {
        this.processBlock(this.pending);
        this.pendingLength = 0;
      }
    }

    while (offset + SHA256_BLOCK_BYTES <= input.byteLength) {
      this.processBlock(input.subarray(offset, offset + SHA256_BLOCK_BYTES));
      offset += SHA256_BLOCK_BYTES;
    }

    if (offset < input.byteLength) {
      const remaining = input.subarray(offset);
      this.pending.set(remaining, 0);
      this.pendingLength = remaining.byteLength;
    }
  }

  digestHex(): string {
    const finalBlocks = new Uint8Array(
      this.pendingLength < 56 ? SHA256_BLOCK_BYTES : SHA256_BLOCK_BYTES * 2,
    );
    finalBlocks.set(this.pending.subarray(0, this.pendingLength));
    finalBlocks[this.pendingLength] = 0x80;

    const bitLengthHigh = Math.floor(this.totalBytes / 0x20000000) >>> 0;
    const bitLengthLow = (this.totalBytes << 3) >>> 0;
    const view = new DataView(finalBlocks.buffer);
    view.setUint32(finalBlocks.byteLength - 8, bitLengthHigh, false);
    view.setUint32(finalBlocks.byteLength - 4, bitLengthLow, false);

    for (let offset = 0; offset < finalBlocks.byteLength; offset += 64) {
      this.processBlock(finalBlocks.subarray(offset, offset + 64));
    }

    return Array.from(this.state, (value) =>
      value.toString(16).padStart(8, "0"),
    ).join("");
  }

  private processBlock(block: Uint8Array): void {
    const words = new Uint32Array(64);
    const view = new DataView(block.buffer, block.byteOffset, block.byteLength);
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(index * 4, false);
    }
    for (let index = 16; index < 64; index += 1) {
      const left = words[index - 15] ?? 0;
      const right = words[index - 2] ?? 0;
      const sigma0 = rotateRight(left, 7) ^ rotateRight(left, 18) ^ (left >>> 3);
      const sigma1 = rotateRight(right, 17) ^ rotateRight(right, 19) ^ (right >>> 10);
      words[index] = (
        (words[index - 16] ?? 0) + sigma0 +
        (words[index - 7] ?? 0) + sigma1
      ) >>> 0;
    }

    let a = this.state[0] ?? 0;
    let b = this.state[1] ?? 0;
    let c = this.state[2] ?? 0;
    let d = this.state[3] ?? 0;
    let e = this.state[4] ?? 0;
    let f = this.state[5] ?? 0;
    let g = this.state[6] ?? 0;
    let h = this.state[7] ?? 0;

    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choose = (e & f) ^ (~e & g);
      const temp1 = (h + sum1 + choose +
        (ROUND_CONSTANTS[index] ?? 0) + (words[index] ?? 0)) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sum0 + majority) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    this.state[0] = ((this.state[0] ?? 0) + a) >>> 0;
    this.state[1] = ((this.state[1] ?? 0) + b) >>> 0;
    this.state[2] = ((this.state[2] ?? 0) + c) >>> 0;
    this.state[3] = ((this.state[3] ?? 0) + d) >>> 0;
    this.state[4] = ((this.state[4] ?? 0) + e) >>> 0;
    this.state[5] = ((this.state[5] ?? 0) + f) >>> 0;
    this.state[6] = ((this.state[6] ?? 0) + g) >>> 0;
    this.state[7] = ((this.state[7] ?? 0) + h) >>> 0;
  }
}

function abortError(): DOMException {
  return new DOMException("操作を取り消しました。", "AbortError");
}

function ensureActive(signal?: AbortSignal): void {
  if (signal?.aborted === true) throw abortError();
}

export async function sha256Blob(
  blob: Blob,
  options: {
    signal?: AbortSignal;
    chunkBytes?: number;
    onProgress?: (processedBytes: number, totalBytes: number) => void;
  } = {},
): Promise<string> {
  const chunkBytes = options.chunkBytes ?? DEFAULT_CHUNK_BYTES;
  if (!Number.isSafeInteger(chunkBytes) || chunkBytes <= 0) {
    throw new RangeError("chunkBytes must be a positive safe integer");
  }

  const hash = new IncrementalSha256();
  options.onProgress?.(0, blob.size);
  for (let offset = 0; offset < blob.size; offset += chunkBytes) {
    ensureActive(options.signal);
    const end = Math.min(offset + chunkBytes, blob.size);
    const bytes = new Uint8Array(await blob.slice(offset, end).arrayBuffer());
    ensureActive(options.signal);
    hash.update(bytes);
    options.onProgress?.(end, blob.size);
  }
  ensureActive(options.signal);
  return hash.digestHex();
}

export interface UploadRequest {
  status: number;
  upload: { onprogress: ((event: ProgressEvent) => void) | null };
  onload: (() => void) | null;
  onerror: (() => void) | null;
  onabort: (() => void) | null;
  open(method: string, url: string, async: boolean): void;
  setRequestHeader(name: string, value: string): void;
  send(body: Blob): void;
  abort(): void;
}

export class UploadTransferError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UploadTransferError";
  }
}

export function putPresignedUpload(
  upload: PresignedRequest & { method: "PUT" },
  file: Blob,
  options: {
    signal?: AbortSignal;
    onProgress?: (uploadedBytes: number, totalBytes: number) => void;
    createRequest?: () => UploadRequest;
  } = {},
): Promise<void> {
  ensureActive(options.signal);
  const request = options.createRequest?.() ?? new XMLHttpRequest();

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (result: "resolve" | "reject", error?: Error) => {
      if (settled) return;
      settled = true;
      options.signal?.removeEventListener("abort", abort);
      request.upload.onprogress = null;
      request.onload = null;
      request.onerror = null;
      request.onabort = null;
      if (result === "resolve") resolve();
      else reject(error ?? new UploadTransferError("動画を送信できませんでした。"));
    };
    const abort = () => {
      request.abort();
      finish("reject", abortError());
    };

    request.open(upload.method, upload.url, true);
    for (const [name, value] of Object.entries(upload.headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      options.onProgress?.(event.loaded, event.lengthComputable ? event.total : file.size);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) finish("resolve");
      else finish(
        "reject",
        new UploadTransferError(
          `動画の送信に失敗しました (HTTP ${String(request.status)})。`,
        ),
      );
    };
    request.onerror = () => {
      finish("reject", new UploadTransferError("動画の送信中に通信エラーが発生しました。"));
    };
    request.onabort = () => {
      finish("reject", abortError());
    };
    options.signal?.addEventListener("abort", abort, { once: true });
    if (options.signal?.aborted === true) {
      abort();
      return;
    }
    options.onProgress?.(0, file.size);
    try {
      request.send(file);
    } catch {
      finish(
        "reject",
        new UploadTransferError("動画の送信を開始できませんでした。"),
      );
    }
  });
}
